import { useState, useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { UseResetPriorityResult } from '@v2/pages/planboard/types';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { backendCall } from '@reducers/utils';
import { MACHINES_PER_REQUEST, RESET_PRIORITY_TIMEOUT_MS } from '@v2/pages/planboard/constants';
import {
  setPendingResetOperation,
  clearMachineRefetch,
  selectMachineColumnsNeedingRefetch,
  fetchMachineOperations,
  setColumnRescheduling,
  clearPendingResetOperation,
} from '../../../../../reducers/v2/planboard';

const useResetPriority = (): UseResetPriorityResult => {
  const [isResetting, setIsResetting] = useState(false);
  const pendingMachineIdRef = useRef<number | null>(null);
  const correlationIdRef = useRef<string | null>(null);
  const dispatch = useAppDispatch();
  const machineColumnsNeedingRefetch = useSelector(selectMachineColumnsNeedingRefetch);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup function for clearing state
  const clearResetState = useCallback(
    (machineId: number, correlationId?: string) => {
      dispatch(setColumnRescheduling({ machineId, isRescheduling: false }));
      // Clean up pendingResetOperations if correlationId provided (e.g., timeout case)
      if (correlationId) {
        dispatch(clearPendingResetOperation(correlationId));
      }
      setIsResetting(false);
      pendingMachineIdRef.current = null;
      correlationIdRef.current = null;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    },
    [dispatch]
  );

  // Watch for completion signal from thunk (thunk already refetched, just clean up local state)
  useEffect(() => {
    const pendingMachineId = pendingMachineIdRef.current;

    if (pendingMachineId && machineColumnsNeedingRefetch?.includes(pendingMachineId)) {
      // Thunk already refetched - just clean up local state and signal
      dispatch(clearMachineRefetch(pendingMachineId));
      setIsResetting(false);
      pendingMachineIdRef.current = null;
      correlationIdRef.current = null;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  }, [machineColumnsNeedingRefetch, dispatch]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const resetPriority = useCallback(
    async (productionOperationId: number, machineId: number) => {
      if (isResetting) {
        return;
      }

      const correlationId = crypto.randomUUID();

      setIsResetting(true);
      pendingMachineIdRef.current = machineId;
      correlationIdRef.current = correlationId;

      // Set column-wide rescheduling state for visual feedback
      dispatch(setColumnRescheduling({ machineId, isRescheduling: true }));

      // Set timeout to auto-clear loading state if SignalR event doesn't arrive
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        // Pass correlationId to clean up pendingResetOperations
        clearResetState(machineId, correlationId);
        // Refetch to get current state
        dispatch(fetchMachineOperations({ machineId, top: MACHINES_PER_REQUEST }));
      }, RESET_PRIORITY_TIMEOUT_MS);

      try {
        // Track this reset operation in Redux
        dispatch(
          setPendingResetOperation({
            correlationId,
            productionOperationId,
            machineId,
          })
        );

        // Call the API
        const call = {
          method: (api: any) => api.productionInformation.resetScheduleProductionOperation,
          params: { correlationId, productionOperationId },
        };
        await dispatch(backendCall(call));

        // Don't set isResetting to false here - wait for SignalR event
        // The useEffect above will handle cleanup when handleFinishedRescheduling thunk completes
      } catch (error) {
        console.error('[useResetPriority] Failed to reset priority:', error);
        clearResetState(machineId, correlationId);
        throw error;
      }
    },
    [dispatch, clearResetState, isResetting]
  );

  return {
    resetPriority,
    isResetting,
  };
};

export default useResetPriority;
